import {
  useOverrideActiveSelectionToGroup,
  useOverrideGroupToActiveSelection
} from '@/plugins/fabric';
import { fabric } from 'fabric';
import { OBJECT_TYPE } from '../constants';
import { getUniqueId, mapObject, pxToIn } from '../utils';
import { DEFAULT_RULE_DATA, fabricToPpObject } from './common';

export const createGroup = activeObj => {
  return new Promise(resolve => {
    useOverrideActiveSelectionToGroup();
    const group = activeObj.toGroup();

    const pPData = {
      type: OBJECT_TYPE.MULTI_OBJECT,
      id: getUniqueId(),
      objects: group.getObjects().map(item => item.id)
    };
    group.set({
      objectType: OBJECT_TYPE.MULTI_OBJECT,
      id: pPData.id,
      lockScalingX: true,
      lockScalingY: true
    });
    const newProp = fabricToPpObject(group);
    resolve({
      newObject: { ...pPData, ...newProp },
      group
    });
  });
};

export const createGroupFabricObject = (ppData, canvas) => {
  return new Promise(resolve => {
    const { objects } = ppData;
    const fabricProp = toFabricGroupProp(ppData);
    useOverrideActiveSelectionToGroup();
    const listObjectCanvasIngroup = canvas
      .getObjects()
      .filter(item => objects.includes(item.id));
    const group = new fabric.Group(listObjectCanvasIngroup, {
      ...fabricProp,
      lockScalingX: true,
      lockScalingY: true
    });
    listObjectCanvasIngroup.forEach(item => {
      canvas.remove(item);
    });
    resolve(group);
  });
};

export const unGroup = activeObject => {
  useOverrideGroupToActiveSelection();
  return activeObject.toActiveSelection();
};

/**
 * Convert stored group properties to fabric properties
 *
 * @param   {Object}  prop  stored image properties
 * @returns {Object}        fabric properties
 */
export const toFabricGroupProp = prop => {
  const mapRules = {
    data: {
      type: DEFAULT_RULE_DATA.TYPE
    },
    restrict: ['size']
  };
  return mapObject(prop, mapRules);
};

export const objectNotInCludesGroup = listObjects => {
  const listGroup = listObjects.filter(
    item => item.objectType === OBJECT_TYPE.MULTI_OBJECT
  );
  const listIdsObjectInGroup = [];
  listGroup.forEach(item => {
    listIdsObjectInGroup.push(...item.objects);
  });
  const newListObject = listObjects.filter(item => {
    return !listIdsObjectInGroup.includes(item.id);
  });
  return newListObject;
};

export const getCoordObjectInCaseHasGroup = (target, group) => {
  if (group) {
    const matrix = group.calcTransformMatrix();
    const { x, y } = fabric.util.transformPoint(
      target.calcACoords().tl,
      matrix
    );
    const coord = {
      x: pxToIn(x),
      y: pxToIn(y)
    };
    return coord;
  } else {
    const coord = {
      x: pxToIn(target.left),
      y: pxToIn(target.top)
    };
    return coord;
  }
};

export const getAngleInCaseHasGroup = (target, group) => {
  if (group) {
    const matrix = group.calcTransformMatrix();
    const { pointObj, currentValue } = getPointToCalcCoord(target);
    const { x, y } = fabric.util.transformPoint(pointObj, matrix);
    const coord = {
      x: pxToIn(x),
      y: pxToIn(y),
      rotation: currentValue.angle
    };
    return coord;
  } else {
    const coord = {
      rotation: target.angle,
      x: pxToIn(target.left),
      y: pxToIn(target.top)
    };
    return coord;
  }
};

const getPointObjectMultiObject = (o, flip) => {
  let pointObj = null;
  if (o.flipX === flip.horizontal && o.flipY === flip.vertical) {
    pointObj = o.calcACoords().tl;
  } else if (o.flipX !== flip.horizontal && o.flipY !== flip.vertical) {
    pointObj = o.calcACoords().br;
  } else if (o.flipX !== flip.horizontal && o.flipY === flip.vertical) {
    pointObj = o.calcACoords().tr;
  } else if (o.flipX === flip.horizontal && o.flipY !== flip.vertical) {
    pointObj = o.calcACoords().bl;
  }
  return pointObj;
};

const getPointObjectInCaseTwoFlipFalseOrTrue = (o, flip) => {
  let pointObj = null;
  if (flip.horizontal && flip.vertical) {
    pointObj = o.calcACoords().tl;
  } else if (flip.horizontal && !flip.vertical) {
    pointObj = o.calcACoords().bl;
  } else if (flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().tr;
  } else if (!flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().br;
  }
  return pointObj;
};

const getPointObjectInCaseFlipY = (o, flip) => {
  let pointObj = null;
  if (flip.horizontal && flip.vertical) {
    pointObj = o.calcACoords().bl;
  } else if (flip.horizontal && !flip.vertical) {
    pointObj = o.calcACoords().tl;
  } else if (flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().br;
  } else if (!flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().tr;
  }
  return pointObj;
};
const getPointObjectInCaseFlipX = (o, flip) => {
  let pointObj = null;
  if (flip.horizontal && flip.vertical) {
    pointObj = o.calcACoords().bl;
  } else if (flip.horizontal && !flip.vertical) {
    pointObj = o.calcACoords().br;
  } else if (flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().tl;
  } else if (!flip.vertical && !flip.horizontal) {
    pointObj = o.calcACoords().tr;
  }
  return pointObj;
};

export const getPointToCalcCoord = o => {
  const currentValue = fabric.util.qrDecompose(o.calcTransformMatrix());
  const flip = {
    horizontal: currentValue.scaleX >= 0 ? false : true,
    vertical: currentValue.scaleY >= 0 ? false : true
  };

  let pointObj = null;
  if (o.objectType !== OBJECT_TYPE.MULTI_OBJECT) {
    pointObj = getPointObjectMultiObject(o, flip);
    // obj has objectType = MultiObject
  } else {
    if ((!o.flipX && !o.flipY) || (o.flipX && o.flipY)) {
      pointObj = getPointObjectInCaseTwoFlipFalseOrTrue(o, flip);
    } else if (!o.flipX && o.flipY) {
      pointObj = getPointObjectInCaseFlipY(o, flip);
    } else if (o.flipX && !o.flipY) {
      pointObj = getPointObjectInCaseFlipX(o, flip);
    }
  }

  return {
    pointObj,
    currentValue,
    flip
  };
};
